/**
 * Hook lab — the hook gets its own panel. Ranked candidates, each a different hook tactic, each shown by its FIRST
 * FRAME (drawn live from the scene library: exactly what the feed shows, sound off). Preview one in the main player,
 * use it, or pick two to four for an A/B set (one MP4 per hook, same body) to test on Meta or TikTok.
 */
import { useState } from 'react';
import { Thumbnail } from '@remotion/player';
import { Check, FlaskConical, Loader2, Play, RefreshCw, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi, type VideoHookCandidate, type VideoProject } from '@/cowork/omniad/api/coworkApi';
import { VideoComposition } from '@/video/VideoComposition';
import { ASPECT_SIZE, VIDEO_FPS, type HookScene, type VideoSpec } from '@/video/spec';

const SCORE_LABELS: Array<[keyof NonNullable<VideoHookCandidate['scores']>, string]> = [['stop', 'Stops'], ['clarity', 'Clear'], ['specificity', 'Specific'], ['tension', 'Tension'], ['fit', 'On brand']];

function FirstFrame({ spec, c, width }: { spec: VideoSpec; c: VideoHookCandidate; width: number }) {
  const size = ASPECT_SIZE[spec.aspect];
  return (
    <Thumbnail
      component={VideoComposition}
      inputProps={{ spec: { ...spec, scenes: [{ ...c.scene, transition: 'cut' }] } }}
      compositionWidth={size.width}
      compositionHeight={size.height}
      durationInFrames={Math.max(2, Math.round(c.scene.duration * VIDEO_FPS))}
      fps={VIDEO_FPS}
      frameToDisplay={1}
      style={{ width, height: (width * size.height) / size.width, borderRadius: 10, overflow: 'hidden' }}
    />
  );
}

export function HookLab({ project, spec, previewId, onPreview, onApplied, onABStarted, busy }: {
  project: VideoProject;
  spec: VideoSpec;
  /** The candidate shown in the main player right now (not applied), if any. */
  previewId: string | null;
  onPreview: (c: VideoHookCandidate | null) => void;
  onApplied: (video: VideoProject) => void;
  onABStarted: (titles: string[]) => void;
  busy?: boolean;
}) {
  const lab = project.hookLab;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [working, setWorking] = useState<string | null>(null);
  const current = spec.scenes.find((s): s is HookScene => s.kind === 'hook');

  async function run() {
    setWorking('run');
    try { await coworkApi.videos.hooks(project.id); } catch (e) { toast.error((e as Error).message); } finally { setWorking(null); }
  }
  async function use(c: VideoHookCandidate) {
    setWorking(c.id);
    try { const { video } = await coworkApi.videos.applyHook(project.id, c.id); onPreview(null); onApplied(video); toast.success(`Applied to scene 1: “${c.scene.text}” (Undo brings the old one back)`); }
    catch (e) { toast.error((e as Error).message); } finally { setWorking(null); }
  }
  async function ab() {
    setWorking('ab');
    try { const { videos } = await coworkApi.videos.hookAB(project.id, [...picked]); setPicked(new Set()); onABStarted(videos.map((v) => v.title)); }
    catch (e) { toast.error((e as Error).message); } finally { setWorking(null); }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[12px] leading-snug text-[#667085]">The first second decides if the rest is seen. Each hook below uses a different tactic and is shown by its <b>first frame</b> — what the feed shows, sound off.</p>
        <button type="button" disabled={busy || lab?.status === 'running' || working === 'run'} onClick={() => void run()} title="Writes and judges six new hooks; they replace the ones below (the hook in the video stays until you change it)" className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[#d0d5dd] px-2 py-1 text-[12px] font-semibold text-[#344054] hover:bg-[#f9fafb] disabled:opacity-40"><RefreshCw className="h-3.5 w-3.5" />{lab ? 'Write 6 new' : 'Run'}</button>
      </div>

      {current && (
        <div className="flex items-center gap-3 rounded-xl bg-[#f9fafb] p-2.5">
          <FirstFrame spec={spec} c={{ id: 'now', tactic: current.tactic || '', tacticName: '', scene: current, backedBy: '', why: '', issues: [] }} width={56} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#667085]">Now in the video</span>
            <span className="text-[13px] font-semibold leading-snug text-[#101828]">“{current.text}”</span>
            {current.tactic && <span className="text-[11.5px] text-[#667085]">{current.tactic.replace(/-/g, ' ')}</span>}
          </div>
        </div>
      )}

      {(!lab || lab.status === 'running') && (
        <div className="flex items-center gap-2 rounded-xl border border-dashed border-[#d0d5dd] p-4 text-[12.5px] text-[#667085]">
          {lab?.status === 'running' || working === 'run' ? <><Loader2 className="h-4 w-4 animate-spin text-[#be43ff]" />Writing hooks, drawing their first frames and judging them… (~20 s)</> : <>No hooks compared yet. Run the lab to see six options side by side.</>}
        </div>
      )}
      {lab?.status === 'failed' && <p className="text-[12.5px] text-[#b42318]">{lab.error}</p>}

      {!!lab?.candidates.length && (
        <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto pr-1">
          {lab.candidates.map((c, i) => {
            const on = previewId === c.id;
            return (
              <div key={c.id} className={`flex gap-3 rounded-xl border p-2.5 ${on ? 'border-[#be43ff] ring-1 ring-[#be43ff]' : 'border-[#eaecf0]'}`}>
                <button type="button" onClick={() => onPreview(on ? null : c)} title={on ? 'Back to the current hook' : 'Preview this hook in the player'} className="relative shrink-0">
                  <FirstFrame spec={spec} c={c} width={96} />
                  <span className="absolute inset-0 grid place-items-center rounded-[10px] bg-black/0 opacity-0 transition-opacity hover:bg-black/30 hover:opacity-100"><Play className="h-5 w-5 text-white" /></span>
                </button>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-[#7a1fbf]">{i === 0 && c.total != null ? 'Best · ' : ''}{c.tacticName}</span>
                    {c.total != null && <span className="shrink-0 rounded-full bg-[#2e024c] px-1.5 py-0.5 text-[11px] font-semibold text-[#a9f600]">{c.total}</span>}
                  </div>
                  <span className="text-[13px] font-semibold leading-snug text-[#101828]">“{c.scene.text}”{c.scene.sub ? <span className="font-normal text-[#475467]"> {c.scene.sub}</span> : null}</span>
                  <span className="text-[11.5px] leading-snug text-[#667085]">{c.why}</span>
                  {c.critique && <span className="text-[11.5px] leading-snug text-[#53389e]">Judge: {c.critique}</span>}
                  {c.scores && (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                      {SCORE_LABELS.map(([k, label]) => <span key={k} className="text-[10.5px] text-[#98a2b3]">{label} <b className={c.scores![k] >= 4 ? 'text-[#3b7d0b]' : c.scores![k] <= 2 ? 'text-[#b42318]' : 'text-[#475467]'}>{c.scores![k]}</b></span>)}
                    </div>
                  )}
                  {!!c.issues.length && <span className="flex items-start gap-1 text-[11px] text-[#b54708]"><TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" />{c.issues[0]}</span>}
                  <span className="text-[10.5px] text-[#98a2b3]">Backed by: {c.backedBy || '—'}</span>
                  <div className="mt-1 flex items-center gap-2">
                    <button type="button" disabled={busy || !!working} onClick={() => void use(c)} className="inline-flex items-center gap-1 rounded-lg bg-[#2e024c] px-2.5 py-1 text-[12px] font-semibold text-white disabled:opacity-40">{working === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 text-[#a9f600]" />}Use this hook</button>
                    <label className="flex items-center gap-1 text-[12px] text-[#475467]"><input type="checkbox" checked={picked.has(c.id)} onChange={(e) => setPicked((cur) => { const n = new Set(cur); if (e.target.checked) n.add(c.id); else n.delete(c.id); return n; })} disabled={!picked.has(c.id) && picked.size >= 4} />A/B</label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!!lab?.candidates.length && (
        <div className="flex items-center justify-between gap-2 border-t border-[#eaecf0] pt-2.5">
          <span className="text-[11.5px] text-[#667085]">{picked.size < 2 ? 'Tick 2–4 hooks to test them against each other.' : `${picked.size} hooks: one MP4 each, same body.`}</span>
          <button type="button" disabled={picked.size < 2 || !!working} onClick={() => void ab()} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#d0d5dd] px-2.5 py-1.5 text-[12px] font-semibold text-[#344054] hover:bg-[#f9fafb] disabled:opacity-40">{working === 'ab' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5 text-[#7a1fbf]" />}Export A/B set</button>
        </div>
      )}
    </div>
  );
}
